import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, TextInput, StyleSheet,
  ActivityIndicator, Alert, ScrollView, Switch, TouchableOpacity, Image,
} from 'react-native';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import {
  updateBranch, uploadBranchImage, uploadBranchBanner,
  deleteStorageUrl, Store,
} from '../query/branchService';
import { flattenBranch } from '../utills/branchAdapter';

type Props = { branchId: string };

const C = {
  primary: '#21808D', success: '#10b981', successLight: '#d1fae5',
  danger: '#ef4444', bg: '#0E1512', card: '#17201B',
  textPrimary: '#E2EDE6', textMuted: '#7A9A80', border: 'rgba(255,255,255,0.09)',
};

const TABS = [
  { key: 'info',     label: 'Basic Info',  icon: 'store' },
  { key: 'charges',  label: 'Charges',     icon: 'payments' },
  { key: 'location', label: 'Location',    icon: 'place' },
  { key: 'content',  label: 'Content',     icon: 'campaign' },
  { key: 'weather',  label: 'Weather',     icon: 'wb_sunny' },
  { key: 'media',    label: 'Media',       icon: 'perm_media' },
];

const MOODS = [
  { id: 'sunny',   label: '☀️  Sunny' },
  { id: 'cloudy',  label: '☁️  Cloudy' },
  { id: 'rainy',   label: '🌧️  Rainy' },
  { id: 'night',   label: '🌙  Night' },
  { id: 'morning', label: '🌅  Morning' },
  { id: 'evening', label: '🌆  Evening' },
];

const Icon = ({ name, size = 18, color = C.textMuted }: { name: string; size?: number; color?: string }) => (
  <span className="material-icons-round" style={{ fontSize: size, color, lineHeight: 1 }}>{name}</span>
);

const Field = ({ label, value, onChange, keyType, multiline, readonly }: any) => (
  <View style={s.fg}>
    <Text style={s.label}>{label}</Text>
    <TextInput
      style={[s.input, multiline && { height: 80, textAlignVertical: 'top' }, readonly && s.inputReadonly]}
      value={value}
      onChangeText={readonly ? undefined : onChange}
      keyboardType={keyType || 'default'}
      multiline={multiline}
      placeholderTextColor={C.textMuted}
      editable={!readonly}
      placeholder={label}
    />
  </View>
);

const Row2 = ({ children }: { children: React.ReactNode }) => (
  <View style={{ flexDirection: 'row', gap: 12 }}>{children}</View>
);

const Half = ({ children }: { children: React.ReactNode }) => (
  <View style={{ flex: 1 }}>{children}</View>
);

export default function BranchScreen({ branchId }: Props) {
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  // Basic Info
  const [storename, setStorename]   = useState('');
  const [phone, setPhone]           = useState('');
  const [pincode, setPincode]       = useState('');
  const [storecate, setStorecate]   = useState('');
  const [storeuid, setStoreuid]     = useState('');
  const [email, setEmail]           = useState('');

  // Charges
  const [delivery, setDelivery]     = useState('0');
  const [packing, setPacking]       = useState('0');
  const [service, setService]       = useState('0');
  const [tax, setTax]               = useState('0');
  const [kmcharge, setKmcharge]     = useState('0');
  const [minAmount, setMinAmount]   = useState('0');
  const [range, setRange]           = useState('0');
  const [radius, setRadius]         = useState('');

  // Location
  const [storeLat, setStoreLat]     = useState('');
  const [storeLon, setStoreLon]     = useState('');

  // Content
  const [announcement, setAnnouncement] = useState('');
  const [policy, setPolicy]         = useState('');
  const [active, setActive]         = useState(true);

  // Banners
  const [bannersEnabled, setBannersEnabled]       = useState(true);
  const [bannerAutoCycle, setBannerAutoCycle]     = useState(true);
  const [bannerShowDots, setBannerShowDots]       = useState(true);
  const [bannerCycleSeconds, setBannerCycleSeconds] = useState(4);

  // Weather
  const [weatherEnabled, setWeatherEnabled]       = useState(true);
  const [weatherMood, setWeatherMood]             = useState('sunny');
  const [weatherSwapDelay, setWeatherSwapDelay]   = useState(5);
  const [weatherLive, setWeatherLive]             = useState(true);

  // Media
  const [logoUrl, setLogoUrl]         = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [bannerImages, setBannerImages]   = useState<string[]>([]);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [stores, setStores]           = useState<Store[]>([]);

  const docUidRef = useRef<string>('');

  useEffect(() => {
    const ref = doc(db, 'zapee_app_settings', 'general');
    return onSnapshot(ref, (snap) => {
      const d = (snap.data() as any) || {};
      setWeatherLive((d.weatherLiveEnabled ?? true) !== false);
    });
  }, []);

  useEffect(() => {
    if (!branchId) { setLoading(false); return; }
    const ref = doc(db, 'zapee_branches', branchId);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const d = flattenBranch(snap.id, snap.data()) as any;
        docUidRef.current = snap.id;
        setEmail(d.email || '');
        setStorename(d.storename || '');
        setPhone(d.phone || '');
        setPincode(d.pincode || '');
        setStorecate(d.storecate || '');
        setStoreuid(d.storeuid || '');
        setDelivery(String(d.delivery ?? 0));
        setPacking(String(d.packing ?? 0));
        setService(String(d.service ?? 0));
        setTax(String(d.tax ?? 0));
        setKmcharge(String(d.kmcharge ?? 0));
        setMinAmount(String(d.minAmount ?? 0));
        setRange(String(d.range ?? 0));
        setRadius(d.radius || '');
        setStoreLat(d.storeLat || '');
        setStoreLon(d.storeLon || '');
        setAnnouncement(d.announcement || '');
        setPolicy(d.policy || '');
        setActive(d.active !== false);
        setBannersEnabled(d.bannersEnabled !== false);
        setBannerAutoCycle(d.bannerAutoCycle !== false);
        setBannerShowDots(d.bannerShowDots !== false);
        setBannerCycleSeconds(Number(d.bannerCycleSeconds ?? 4));
        setWeatherEnabled(d.weatherEnabled !== false);
        setWeatherMood(d.weather || 'sunny');
        setWeatherSwapDelay(Number(d.weatherSwapDelaySeconds ?? 5));
        setLogoUrl(d.image || '');
        setBannerImages(Array.isArray(d.bannerImages) ? d.bannerImages : []);
        setStores(Array.isArray(d.store) ? d.store : []);
      }
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [branchId]);

  const handleSave = async () => {
    if (!branchId) return;
    setSaving(true);
    try {
      await updateBranch(branchId, {
        storename, phone, pincode, storecate, storeuid,
        delivery: parseInt(delivery) || 0,
        packing:  parseInt(packing)  || 0,
        service:  parseInt(service)  || 0,
        tax:      parseInt(tax)      || 0,
        kmcharge: parseInt(kmcharge) || 0,
        minAmount:parseInt(minAmount)|| 0,
        range:    parseInt(range)    || 0,
        radius, storeLat, storeLon,
        announcement, policy, active,
        bannersEnabled, bannerAutoCycle, bannerShowDots, bannerCycleSeconds,
        weatherEnabled, weather: weatherMood, weatherSwapDelaySeconds: weatherSwapDelay,
        image: logoUrl, bannerImages, store: stores,
      });
      await setDoc(doc(db, 'zapee_app_settings', 'general'), { weatherLiveEnabled: weatherLive }, { merge: true });
      Alert.alert('Saved', 'Branch settings updated.');
    } catch (e: any) {
      Alert.alert('Error', e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleLogo = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*';
    input.onchange = async (e: any) => {
      const file: File = e.target.files[0];
      if (!file) return;
      setLogoUploading(true);
      try { setLogoUrl(await uploadBranchImage(file, branchId)); }
      catch { Alert.alert('Error', 'Logo upload failed'); }
      finally { setLogoUploading(false); }
    };
    input.click();
  };

  const handleBannerAdd = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*'; input.multiple = true;
    input.onchange = async (e: any) => {
      const files: File[] = Array.from(e.target.files);
      if (!files.length) return;
      setBannerUploading(true);
      try {
        const urls = await Promise.all(files.map((f, i) => uploadBranchBanner(f, branchId, bannerImages.length + i)));
        setBannerImages(prev => [...prev, ...urls]);
      } catch { Alert.alert('Error', 'Banner upload failed'); }
      finally { setBannerUploading(false); }
    };
    input.click();
  };

  const removeBanner = async (idx: number) => {
    await deleteStorageUrl(bannerImages[idx]);
    setBannerImages(prev => prev.filter((_, i) => i !== idx));
  };

  const addStore    = () => setStores(prev => [...prev, { name: '', image: '' }]);
  const removeStore = (idx: number) => setStores(prev => prev.filter((_, i) => i !== idx));
  const updateStoreField = (idx: number, k: keyof Store, v: string) =>
    setStores(prev => prev.map((s, i) => i === idx ? { ...s, [k]: v } : s));

  if (loading) return (
    <View style={s.center}>
      <ActivityIndicator size="large" color={C.primary} />
    </View>
  );

  if (!branchId) return (
    <View style={s.center}>
      <Text style={{ color: C.textMuted, fontSize: 15 }}>No branch assigned to your account.</Text>
    </View>
  );

  const renderTab = () => {
    switch (activeTab) {
      case 'info':
        return (
          <View>
            <Field label="Store Name" value={storename} onChange={setStorename} />
            <Field label="Email" value={email} readonly />
            <Row2>
              <Half><Field label="Phone" value={phone} onChange={setPhone} keyType="phone-pad" /></Half>
              <Half><Field label="Pincode" value={pincode} onChange={setPincode} /></Half>
            </Row2>
            <Row2>
              <Half><Field label="Category" value={storecate} onChange={setStorecate} /></Half>
              <Half><Field label="Store UID" value={storeuid} onChange={setStoreuid} /></Half>
            </Row2>
          </View>
        );
      case 'charges':
        return (
          <View>
            <Row2>
              <Half><Field label="Delivery (₹)" value={delivery} onChange={setDelivery} keyType="numeric" /></Half>
              <Half><Field label="Packing (₹)" value={packing} onChange={setPacking} keyType="numeric" /></Half>
            </Row2>
            <Row2>
              <Half><Field label="Service (₹)" value={service} onChange={setService} keyType="numeric" /></Half>
              <Half><Field label="KM Charge (₹)" value={kmcharge} onChange={setKmcharge} keyType="numeric" /></Half>
            </Row2>
            <Row2>
              <Half><Field label="Min Order (₹)" value={minAmount} onChange={setMinAmount} keyType="numeric" /></Half>
              <Half><Field label="Tax (%)" value={tax} onChange={setTax} keyType="numeric" /></Half>
            </Row2>
            <Row2>
              <Half><Field label="Range (km)" value={range} onChange={setRange} keyType="numeric" /></Half>
              <Half><Field label="Radius" value={radius} onChange={setRadius} /></Half>
            </Row2>
          </View>
        );
      case 'location':
        return (
          <View>
            <Row2>
              <Half><Field label="Latitude" value={storeLat} onChange={setStoreLat} keyType="decimal-pad" /></Half>
              <Half><Field label="Longitude" value={storeLon} onChange={setStoreLon} keyType="decimal-pad" /></Half>
            </Row2>
            <Field label="Pincode" value={pincode} onChange={setPincode} />
          </View>
        );
      case 'content':
        return (
          <View>
            <Field label="Announcement" value={announcement} onChange={setAnnouncement} multiline />
            <Field label="Policy" value={policy} onChange={setPolicy} multiline />
            <View style={s.switchRow}>
              <View>
                <Text style={s.switchLabel}>Active</Text>
                <Text style={s.switchSub}>Branch is visible in the customer app</Text>
              </View>
              <Switch
                value={active} onValueChange={setActive}
                trackColor={{ false: C.border, true: C.success }} thumbColor="#fff"
              />
            </View>
          </View>
        );
      case 'weather':
        return (
          <View>
            <View style={s.switchRow}>
              <View>
                <Text style={s.switchLabel}>Show Weather Backdrop</Text>
                <Text style={s.switchSub}>Animated sky behind the storefront</Text>
              </View>
              <Switch value={weatherEnabled} onValueChange={setWeatherEnabled}
                trackColor={{ false: C.border, true: C.primary }} thumbColor="#fff" />
            </View>
            <View style={[s.switchRow, { marginTop: 10 }]}>
              <View>
                <Text style={s.switchLabel}>Use Live Weather API</Text>
                <Text style={s.switchSub}>{weatherLive ? 'Follows real conditions (Google)' : 'Follows the clock — no API'}</Text>
              </View>
              <Switch value={weatherLive} onValueChange={setWeatherLive}
                trackColor={{ false: C.border, true: C.primary }} thumbColor="#fff" />
            </View>
            <Text style={[s.sectionLabel, { marginTop: 20 }]}>Weather Mood</Text>
            <View style={s.moodGrid}>
              {MOODS.map(m => {
                const active = weatherMood === m.id;
                return (
                  <TouchableOpacity key={m.id} style={[s.moodChip, active && s.moodChipActive]}
                    onPress={() => setWeatherMood(m.id)}>
                    <Text style={[s.moodChipTxt, active && s.moodChipTxtActive]}>{m.label}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <Text style={[s.sectionLabel, { marginTop: 20 }]}>Weather → Banner Delay</Text>
            <View style={s.stepper}>
              <TouchableOpacity style={s.stepBtn} onPress={() => setWeatherSwapDelay(v => Math.max(0, v - 1))}>
                <Text style={s.stepSign}>−</Text>
              </TouchableOpacity>
              <Text style={s.stepVal}>{weatherSwapDelay} s</Text>
              <TouchableOpacity style={s.stepBtn} onPress={() => setWeatherSwapDelay(v => Math.min(30, v + 1))}>
                <Text style={s.stepSign}>+</Text>
              </TouchableOpacity>
            </View>
          </View>
        );
      case 'media':
        return (
          <View>
            {/* Banner Settings */}
            <Text style={s.sectionLabel}>Banner Settings</Text>
            <View style={s.switchRow}>
              <View>
                <Text style={s.switchLabel}>Show Banners</Text>
                <Text style={s.switchSub}>Display banner images in the storefront</Text>
              </View>
              <Switch value={bannersEnabled} onValueChange={setBannersEnabled}
                trackColor={{ false: C.border, true: C.primary }} thumbColor="#fff" />
            </View>
            <View style={[s.switchRow, { marginTop: 10 }]}>
              <View>
                <Text style={s.switchLabel}>Auto-Cycle</Text>
                <Text style={s.switchSub}>Automatically scroll through banners</Text>
              </View>
              <Switch value={bannerAutoCycle} onValueChange={setBannerAutoCycle}
                trackColor={{ false: C.border, true: C.primary }} thumbColor="#fff" />
            </View>
            <View style={[s.switchRow, { marginTop: 10 }]}>
              <View>
                <Text style={s.switchLabel}>Show Dot Indicators</Text>
                <Text style={s.switchSub}>Page dots below the banner</Text>
              </View>
              <Switch value={bannerShowDots} onValueChange={setBannerShowDots}
                trackColor={{ false: C.border, true: C.primary }} thumbColor="#fff" />
            </View>
            <Text style={[s.sectionLabel, { marginTop: 20 }]}>Cycle Interval</Text>
            <View style={s.stepper}>
              <TouchableOpacity style={s.stepBtn} onPress={() => setBannerCycleSeconds(v => Math.max(2, v - 1))}>
                <Text style={s.stepSign}>−</Text>
              </TouchableOpacity>
              <Text style={s.stepVal}>{bannerCycleSeconds} s</Text>
              <TouchableOpacity style={s.stepBtn} onPress={() => setBannerCycleSeconds(v => Math.min(15, v + 1))}>
                <Text style={s.stepSign}>+</Text>
              </TouchableOpacity>
            </View>

            <View style={s.divider} />

            {/* Logo */}
            <Text style={s.sectionLabel}>Branch Logo</Text>
            <View style={s.logoSection}>
              <TouchableOpacity style={s.logoWrap} onPress={handleLogo} disabled={logoUploading}>
                {logoUploading
                  ? <ActivityIndicator color={C.primary} />
                  : logoUrl
                    ? <Image source={{ uri: logoUrl }} style={s.logoImg} />
                    : <View style={s.logoEmpty}>
                        <Icon name="add_photo_alternate" size={28} color={C.primary} />
                        <Text style={s.logoHint}>Upload Logo</Text>
                      </View>
                }
              </TouchableOpacity>
              {logoUrl ? (
                <TouchableOpacity onPress={async () => { await deleteStorageUrl(logoUrl); setLogoUrl(''); }}>
                  <Text style={s.removeText}>Remove</Text>
                </TouchableOpacity>
              ) : null}
            </View>

            {/* Banners */}
            <Text style={s.sectionLabel}>Banner Images</Text>
            {bannerImages.map((url, idx) => (
              <View key={idx} style={s.bannerRow}>
                {url ? <Image source={{ uri: url }} style={s.bannerThumb} /> : null}
                <Text style={s.bannerUrl} numberOfLines={1}>{url}</Text>
                <TouchableOpacity onPress={() => removeBanner(idx)} style={s.bannerDel}>
                  <Icon name="close" size={14} color={C.danger} />
                </TouchableOpacity>
              </View>
            ))}
            <TouchableOpacity style={[s.dashedBtn, bannerUploading && { opacity: 0.6 }]} onPress={handleBannerAdd} disabled={bannerUploading}>
              {bannerUploading
                ? <ActivityIndicator color={C.primary} size="small" />
                : <Text style={s.dashedBtnTxt}>+ Upload Banners</Text>
              }
            </TouchableOpacity>

            {/* Linked Stores */}
            <Text style={[s.sectionLabel, { marginTop: 20 }]}>Linked Stores</Text>
            {stores.map((st, idx) => (
              <View key={idx} style={s.storeEntry}>
                <View style={{ flex: 1 }}>
                  <Field label="Store Name" value={st.name} onChange={(v: string) => updateStoreField(idx, 'name', v)} />
                  <Field label="Image URL"  value={st.image} onChange={(v: string) => updateStoreField(idx, 'image', v)} />
                </View>
                <TouchableOpacity onPress={() => removeStore(idx)} style={s.storeRemove}>
                  <Icon name="close" size={14} color={C.danger} />
                </TouchableOpacity>
              </View>
            ))}
            <TouchableOpacity style={s.dashedBtn} onPress={addStore}>
              <Text style={s.dashedBtnTxt}>+ Add Store</Text>
            </TouchableOpacity>
          </View>
        );
      default: return null;
    }
  };

  return (
    <View style={s.container}>
      {/* Header */}
      <View style={s.header}>
        <Icon name="location_city" size={20} color="#4ECDC4" />
        <View style={{ flex: 1 }}>
          <Text style={s.headerTitle}>{storename || 'Branch Settings'}</Text>
          <Text style={s.headerSub}>Branch ID: {branchId}</Text>
        </View>
        <View style={[s.statusPill, { backgroundColor: active ? C.successLight : '#f1f5f9' }]}>
          <View style={[s.statusDot, { backgroundColor: active ? C.success : '#94a3b8' }]} />
          <Text style={[s.statusTxt, { color: active ? C.success : '#64748b' }]}>
            {active ? 'Active' : 'Inactive'}
          </Text>
        </View>
      </View>

      {/* Tabs */}
      <View style={s.tabBar}>
        {TABS.map(tab => {
          const active = activeTab === tab.key;
          return (
            <TouchableOpacity key={tab.key} style={[s.tab, active && s.tabActive]} onPress={() => setActiveTab(tab.key)}>
              <Icon name={tab.icon} size={16} color={active ? '#fff' : C.textMuted} />
              <Text style={[s.tabLabel, active && s.tabLabelActive]}>{tab.label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Content */}
      <ScrollView style={{ flex: 1 }} contentContainerStyle={s.content} showsVerticalScrollIndicator={false}>
        {renderTab()}
      </ScrollView>

      {/* Save Bar */}
      <View style={s.saveBar}>
        <TouchableOpacity style={[s.saveBtn, saving && { opacity: 0.6 }]} onPress={handleSave} disabled={saving}>
          {saving
            ? <ActivityIndicator color="#fff" size="small" />
            : <><Icon name="save" size={16} color="#fff" /><Text style={s.saveBtnTxt}>Save Changes</Text></>
          }
        </TouchableOpacity>
      </View>
    </View>
  );
}

const s = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.bg, flexDirection: 'column' },
  center:    { flex: 1, justifyContent: 'center', alignItems: 'center' },

  header: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: '#0A1712', paddingHorizontal: 16, paddingVertical: 12,
    borderBottomWidth: 1, borderBottomColor: 'rgba(33,128,141,0.18)',
  },
  headerTitle: { fontSize: 15, fontWeight: '800', color: '#fff' },
  headerSub:   { fontSize: 11, color: '#8BAAB8', marginTop: 1 },
  statusPill:  { flexDirection: 'row', alignItems: 'center', gap: 5, borderRadius: 20, paddingHorizontal: 10, paddingVertical: 4 },
  statusDot:   { width: 7, height: 7, borderRadius: 4 },
  statusTxt:   { fontSize: 12, fontWeight: '600' },

  tabBar: {
    flexDirection: 'row', backgroundColor: '#0A1712',
    borderBottomWidth: 1, borderBottomColor: 'rgba(33,128,141,0.18)',
    paddingHorizontal: 12, paddingTop: 8, gap: 4,
  },
  tab: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8,
    marginBottom: 6,
  },
  tabActive:      { backgroundColor: '#047E33' },
  tabLabel:       { fontSize: 13, color: C.textMuted, fontWeight: '500' },
  tabLabelActive: { color: '#fff', fontWeight: '700' },

  content: { padding: 20, paddingBottom: 16 },

  fg:    { marginBottom: 14 },
  label: { fontSize: 13, fontWeight: '600', color: '#A8C4AF', marginBottom: 6 },
  input: {
    borderWidth: 1, borderColor: C.border, borderRadius: 10,
    paddingHorizontal: 14, paddingVertical: 11,
    fontSize: 14, color: C.textPrimary, backgroundColor: 'rgba(255,255,255,0.04)',
  },
  inputReadonly: { backgroundColor: 'rgba(255,255,255,0.02)', color: C.textMuted },

  switchRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: 14, paddingHorizontal: 14, marginBottom: 4,
    borderWidth: 1, borderColor: C.border, borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  switchLabel: { fontSize: 14, fontWeight: '600', color: C.textPrimary },
  switchSub:   { fontSize: 12, color: C.textMuted, marginTop: 2 },

  sectionLabel: {
    fontSize: 11, fontWeight: '800', color: C.primary,
    letterSpacing: 0.8, marginBottom: 10,
    textTransform: 'uppercase' as const,
  },

  logoSection:    { alignItems: 'center', marginBottom: 16 },
  logoWrap:       { width: 100, height: 100, borderRadius: 16, backgroundColor: 'rgba(33,128,141,0.1)', justifyContent: 'center', alignItems: 'center', borderWidth: 2, borderColor: C.primary, borderStyle: 'dashed' as const, overflow: 'hidden' },
  logoImg:        { width: 100, height: 100, borderRadius: 14 },
  logoEmpty:      { alignItems: 'center', gap: 4 },
  logoHint:       { fontSize: 11, color: C.primary, fontWeight: '600', marginTop: 2 },
  removeText:     { fontSize: 12, color: C.danger, fontWeight: '600', marginTop: 8 },

  bannerRow:    { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8, backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 8 },
  bannerThumb:  { width: 56, height: 36, borderRadius: 6, backgroundColor: '#1e2d25' },
  bannerUrl:    { flex: 1, fontSize: 12, color: C.textMuted },
  bannerDel:    { width: 26, height: 26, borderRadius: 13, backgroundColor: 'rgba(239,68,68,0.12)', justifyContent: 'center', alignItems: 'center' },

  dashedBtn:    { borderWidth: 1, borderColor: C.primary, borderStyle: 'dashed' as const, borderRadius: 10, paddingVertical: 12, alignItems: 'center', marginBottom: 8 },
  dashedBtnTxt: { color: C.primary, fontWeight: '600', fontSize: 13 },

  storeEntry:    { flexDirection: 'row', gap: 10, backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: 12, marginBottom: 10 },
  storeRemove:   { width: 30, height: 30, borderRadius: 15, backgroundColor: 'rgba(239,68,68,0.12)', justifyContent: 'center', alignItems: 'center', marginTop: 32 },

  divider: { height: 1, backgroundColor: 'rgba(255,255,255,0.07)', marginVertical: 20 },

  moodGrid:        { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  moodChip:        { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, borderWidth: 1, borderColor: C.border, backgroundColor: 'rgba(255,255,255,0.04)' },
  moodChipActive:  { backgroundColor: '#21808D', borderColor: '#21808D' },
  moodChipTxt:     { fontSize: 13, fontWeight: '600', color: C.textMuted },
  moodChipTxtActive: { color: '#fff' },

  stepper:  { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', borderWidth: 1, borderColor: C.border, borderRadius: 10, overflow: 'hidden' },
  stepBtn:  { paddingHorizontal: 18, paddingVertical: 10 },
  stepSign: { fontSize: 20, fontWeight: '800', color: C.primary },
  stepVal:  { minWidth: 70, textAlign: 'center', fontSize: 15, fontWeight: '800', color: C.textPrimary },

  saveBar: { padding: 14, borderTopWidth: 1, borderTopColor: 'rgba(33,128,141,0.2)', backgroundColor: '#0A1712' },
  saveBtn: {
    backgroundColor: C.primary, borderRadius: 12, paddingVertical: 13,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
  },
  saveBtnTxt: { color: '#fff', fontWeight: '700', fontSize: 15 },
});
